import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { User, Save, Zap, Star, Award, Settings, LogOut } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MSProfilePage() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    nickname: '',
    grade: '',
    city: '',
    state: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || '',
        nickname: user.nickname || user.full_name?.split(' ')[0] || '',
        grade: user.grade?.toString() || '',
        city: user.city || '',
        state: user.state || ''
      });
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile`, {
        full_name: formData.full_name,
        nickname: formData.nickname,
        grade: formData.grade ? parseInt(formData.grade) : null,
        city: formData.city,
        state: formData.state
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile saved! 🎉');
    } catch (error) {
      toast.error('Oops! Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const xpPoints = user?.xp_points || 0;
  const level = Math.floor(xpPoints / 100) + 1;
  const xpToNextLevel = 100 - (xpPoints % 100);

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 text-center"
      >
        <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4">
          <User className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-white">
          {formData.nickname || formData.full_name || 'Explorer'}
        </h1>
        <p className="text-slate-300">Grade {formData.grade || '?'} Explorer</p>
      </motion.div>

      {/* XP & Level Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="p-6 rounded-2xl bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 mb-6"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-white font-bold text-xl">{xpPoints} XP</p>
              <p className="text-yellow-200 text-sm">Level {level}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-yellow-200 text-sm">{xpToNextLevel} XP to Level {level + 1}</p>
          </div>
        </div>
        <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-yellow-400 to-orange-500"
            initial={{ width: 0 }}
            animate={{ width: `${((xpPoints % 100) / 100) * 100}%` }}
          />
        </div>
      </motion.div>

      {/* Quick Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-3 gap-4 mb-6"
      >
        <div className="p-4 text-center rounded-xl bg-gradient-to-br from-yellow-500/20 to-amber-500/20 border border-yellow-500/30">
          <Star className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
          <p className="text-white font-bold">{level}</p>
          <p className="text-yellow-200 text-xs">Level</p>
        </div>
        <div className="p-4 text-center rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30">
          <Award className="w-6 h-6 text-purple-400 mx-auto mb-2" />
          <p className="text-white font-bold">2</p>
          <p className="text-purple-200 text-xs">Badges</p>
        </div>
        <div className="p-4 text-center rounded-xl bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/30">
          <Zap className="w-6 h-6 text-orange-400 mx-auto mb-2" />
          <p className="text-white font-bold">{xpPoints}</p>
          <p className="text-orange-200 text-xs">Total XP</p>
        </div>
      </motion.div>

      {/* Edit Profile Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="p-6 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30"
      >
        <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Edit Profile
        </h2>

        <div className="space-y-4">
          <div>
            <Label className="text-slate-300">Full Name</Label>
            <Input
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              className="input-dark mt-1"
              data-testid="profile-name"
            />
          </div>

          <div>
            <Label className="text-slate-300">Nickname (what we'll call you)</Label>
            <Input
              value={formData.nickname}
              onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
              placeholder="Your nickname"
              className="input-dark mt-1"
              data-testid="profile-nickname"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Grade</Label>
              <Input
                type="number"
                min="6"
                max="8"
                value={formData.grade}
                onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                className="input-dark mt-1"
                data-testid="profile-grade"
              />
            </div>
            <div>
              <Label className="text-slate-300">City</Label>
              <Input
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="input-dark mt-1"
                data-testid="profile-city"
              />
            </div>
          </div>

          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
          >
            {saving ? 'Saving...' : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </motion.div>

      {/* Logout */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="mt-6"
      >
        <Button
          variant="outline"
          onClick={handleLogout}
          className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Log Out
        </Button>
      </motion.div>
    </div>
  );
}
